import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { errDetail } from './adminShared';

// Superadmin-only "Delete All" button with a type-DELETE confirmation dialog.
export const DeleteAllButton = ({ label, title, description, url, onDone, testId }) => {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);

  if (localStorage.getItem('username') !== 'superadmin') return null;

  const handleDelete = async () => {
    setLoading(true);
    try {
      const res = await axios.delete(url, { withCredentials: true });
      const counts = Object.entries(res.data)
        .filter(([k, v]) => k.endsWith('_deleted') && typeof v === 'number')
        .map(([k, v]) => `${v} ${k.replace('_deleted', '')}`)
        .join(', ');
      toast.success(`Deleted: ${counts || 'done'}`);
      setOpen(false);
      onDone();
    } catch (error) {
      toast.error(errDetail(error, 'Bulk delete failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="border-red-400 text-red-600 hover:bg-red-50"
        onClick={() => { setConfirmText(''); setOpen(true); }}
        data-testid={testId}
      >
        <Trash2 className="w-4 h-4 mr-2" />
        {label}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid={`${testId}-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              {title}
            </DialogTitle>
            <DialogDescription className="whitespace-pre-line">{description}</DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-sm font-medium text-gray-700">Type <span className="font-bold text-red-600">DELETE</span> to confirm</label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              className="mt-1"
              data-testid={`${testId}-confirm-input`}
              autoFocus
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid={`${testId}-cancel`}>Cancel</Button>
            <Button
              onClick={handleDelete}
              disabled={confirmText !== 'DELETE' || loading}
              className="bg-red-600 hover:bg-red-700"
              data-testid={`${testId}-confirm`}
            >
              {loading ? 'Deleting…' : 'Delete Permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
